import moment from 'moment'

import { getPaymentPeriodLabels } from '@elo-kit/constants/paymentSetting.constants'
import { PAYMENT_PLANS } from '@elo-kit/constants/pricingPlans.constants'
import { SELECT_INTERVAL_OPTIONS } from 'constants/orders.constants'
import { parseShrinkDate } from './dateTime.utils'

export const getPricingPlanName = (pricingPlanProperties: {
  form: string
  name: string
  pricingPlan: any // TODO:
}): string =>
  pricingPlanProperties.pricingPlan[`name${I18n.locale.replace(/\b\w/g, (l) => l.toUpperCase())}`] ||
  pricingPlanProperties.name ||
  getPaymentPeriodLabels()[pricingPlanProperties.form]

export const getPricingPlanDescription = (pricingPlanProperties: {
  description: string
  pricingPlan: any // TODO:
}): string =>
  pricingPlanProperties?.pricingPlan[`description${I18n.locale.replace(/\b\w/g, (l) => l.toUpperCase())}`] ||
  pricingPlanProperties?.description

// 30-day months and 360-day years keep "1y" equal to 12 monthly payments
const SHRINK_DATE_UNIT_DAYS = {
  d: 1,
  w: 7,
  m: 30,
  y: 360,
}

const shrinkDateToDays = (period?: string | number): number => {
  const { number, letter } = parseShrinkDate(String(period ?? ''))
  const unitDays = SHRINK_DATE_UNIT_DAYS[letter]

  if (!unitDays || !Number(number)) return 0

  return Number(number) * unitDays
}

export const LIMITED_SUBSCRIPTION_MAX_MINIMUM_TERM_MONTHS = 24

export const LIMITED_SUBSCRIPTION_DEFAULT_MINIMUM_TERM = `${LIMITED_SUBSCRIPTION_MAX_MINIMUM_TERM_MONTHS}m`

export const hasCustomLimitedSubscriptionMinimumTerm = (period?: string): boolean =>
  !!period && String(period) !== LIMITED_SUBSCRIPTION_DEFAULT_MINIMUM_TERM

export const getLimitedSubscriptionMinimumTermMax = (unit: string): number => {
  const unitDays = SHRINK_DATE_UNIT_DAYS[unit] || SHRINK_DATE_UNIT_DAYS.m

  return Math.floor((LIMITED_SUBSCRIPTION_MAX_MINIMUM_TERM_MONTHS * SHRINK_DATE_UNIT_DAYS.m) / unitDays)
}

export const clampLimitedSubscriptionMinimumTerm = (value: string | number, unit: string): number => {
  const numericValue = parseInt(String(value), 10)

  if (!numericValue) return 0

  return Math.min(numericValue, getLimitedSubscriptionMinimumTermMax(unit))
}

const getCustomIntervalsDurationDays = (prefs: object, paymentsCount: number): number => {
  const rateIntervalsDays = Array.from({ length: paymentsCount }, (_, index) =>
    shrinkDateToDays(prefs[`rate${index + 1}Interval`])
  )

  if (rateIntervalsDays.some((days) => !days)) return 0

  return rateIntervalsDays.reduce((total, days) => total + days, 0)
}

const getFirstAbsolutePaymentDate = (
  prefs: { firstPaymentOnOrderDate?: boolean },
  firstIntervalDate?: string
): moment.Moment | null => {
  const firstRateDate = prefs['rate1Date']

  if (firstRateDate) return moment(firstRateDate)
  if (prefs.firstPaymentOnOrderDate && firstIntervalDate) return moment(firstIntervalDate, 'DD.MM.YYYY')

  return null
}

const isMinimumTermCoveringAbsoluteDates = (
  prefs: {
    minimumTermInitialPeriod?: string | number
    firstPaymentOnOrderDate?: boolean
  },
  paymentsCount: number,
  firstIntervalDate?: string
): boolean => {
  const { number, letter } = parseShrinkDate(String(prefs.minimumTermInitialPeriod ?? ''))
  const unit = SELECT_INTERVAL_OPTIONS[letter]
  const lastRateDate = prefs[`rate${paymentsCount}Date`]
  const firstPaymentDate = getFirstAbsolutePaymentDate(prefs, firstIntervalDate)

  if (!unit || !Number(number) || !firstPaymentDate?.isValid() || !lastRateDate) return false

  const lastPaymentDate = moment(lastRateDate)

  if (!lastPaymentDate.isValid()) return false

  return firstPaymentDate.add(Number(number), unit).isSameOrAfter(lastPaymentDate, 'day')
}

const getRegularIntervalsDurationDays = (
  prefs: { firstInterval?: string; nextInterval?: string },
  paymentsCount: number
): number => {
  const firstIntervalDays = shrinkDateToDays(prefs.firstInterval)
  const nextIntervalDays = shrinkDateToDays(prefs.nextInterval) || firstIntervalDays

  if (!firstIntervalDays) return 0

  return firstIntervalDays + nextIntervalDays * (paymentsCount - 1)
}

export const isMinimumTermCoveringWholePlan = (pricingPlan?: {
  form?: string
  firstIntervalDate?: string
  prefs?: {
    minimumTermInitialPeriod?: string | number
    firstInterval?: string
    nextInterval?: string
    paymentsCount?: string | number
    customIntervals?: boolean
    absoluteDates?: boolean
    firstPaymentOnOrderDate?: boolean
  }
}): boolean => {
  const { form, firstIntervalDate, prefs = {} } = pricingPlan || {}

  if (form !== PAYMENT_PLANS.limitedSubscription) return false

  const paymentsCount = parseInt(String(prefs.paymentsCount), 10)
  const minimumTermDays = shrinkDateToDays(prefs.minimumTermInitialPeriod)

  if (!paymentsCount || !minimumTermDays) return false

  if (prefs.customIntervals && prefs.absoluteDates) {
    return isMinimumTermCoveringAbsoluteDates(prefs, paymentsCount, firstIntervalDate)
  }

  const planDurationDays = prefs.customIntervals
    ? getCustomIntervalsDurationDays(prefs, paymentsCount)
    : getRegularIntervalsDurationDays(prefs, paymentsCount)

  if (!planDurationDays) return false

  return minimumTermDays >= planDurationDays
}

export const isAbsdFeatureAvailable = (isFeatureEnabled: boolean, isLimitedSubscription: boolean): boolean =>
  isFeatureEnabled && isLimitedSubscription

export const isAbsoluteStartDateActive = (
  isFeatureEnabled: boolean,
  isLimitedSubscription: boolean,
  absoluteStartDate?: string
): boolean => isAbsdFeatureAvailable(isFeatureEnabled, isLimitedSubscription) && !!absoluteStartDate

export const isPricingPlanAvailabilityRequired = ({
  absoluteDates,
  absoluteStartDate,
  isAbsdFeatureEnabled,
  isLimitedSubscription,
}: {
  absoluteDates?: boolean
  absoluteStartDate?: string
  isAbsdFeatureEnabled: boolean
  isLimitedSubscription: boolean
}): boolean =>
  !!absoluteDates || isAbsoluteStartDateActive(isAbsdFeatureEnabled, isLimitedSubscription, absoluteStartDate)
